<template>
  <div 
    aria-label="video player"
    tabindex="0"
    :class="[
      'videoContainer',
      { hidden: playing && !uiLocked && uiHidden }
    ]"
    @mousemove="handleMovement"
    @mousedown="handleMovement"
    @keydown="handleMovement"
    @click="playing = !playing"
    @keydown.space="handleMovement; playing = !playing"
    @keydown.right="handleMovement; desiresTime = currentTime + 10"
    @keydown.left="handleMovement; desiresTime = currentTime - 10"
  >
    <div 
      class="header"
      tabindex="0"
    >
      <div class="title"><span> {{ info.title }} </span></div>
      <div class="descr"><span> {{ info.descr }} </span></div>
    </div>
    <video
      preload="metadata"
      crossorigin="anonymous"
      ref="video"
      :src="videoURL"
      @loadedmetadata="handleLoaded"
      @timeupdate="handleTimeUpdate"
    >
      <track
        v-if="track"
        default 
        srclang="en" 
        kind="captions" 
        label="English" 
        ref="track"
        :src="trackSRC"
        @cuechange="handleCueChange"
      />
    </video>
    <div class="controls">
      <div 
        v-if="trackSRC"
        class="cueContainer"
      >
        <span class="cue">{{ currentCue }}</span>
      </div>
      <Button
        v-if="playing"
        @click.stop="pause"
        @keydown.space.stop="pause"
        @keydown.enter.stop="pause"
        icon="pause"
      />
      <Button
        v-else
        @click.stop="play"
        @keydown.space.stop="play"
        @keydown.enter.stop="play"        
        icon="play"
      />
      <Tracker
       :duration="duration"
       :currentTime="currentTime"
       @desiresTime="desiresTime = $event"
      />
    </div>
  </div>
</template>
<script>

import captions from '../../../utils/captions'
import Button from '../Button'
import Tracker from './Tracker'

export default {
  
  name: 'Video',

  components: { 
    Button,
    Tracker
  },

  props: [
    'info',
    'video',
    'track',
  ],

  data() {
    return {
      trackSRC: null,

      duration: 0,
      currentTime: 0,
      desiresTime: null,
      playing: false,
      currentCue: null,
      
      timeout: null,
      uiLocked: false,
      uiHidden: false,
    }
  },

  computed: {
    videoURL() { return this.$apiURL + this.video.url },
    trackURL() { return this.track && this.$apiURL + this.track.url },
  },

  methods: {
    ...captions,

    play() {
      this.$refs.video.play()
      this.playing = true
    },
    pause() {
      this.$refs.video.pause()
      this.playing = false
    },

    handleLoaded(e) {
      if (e.target.textTracks[0]) {
        e.target.textTracks[0].mode = 'hidden'
        this.duration = e.target.duration
      }
    },

    handleTimeUpdate(e) {
      this.currentTime = e.target.currentTime
      this.duration = e.target.duration
    },

    handleCueChange(e) {
      const cue = e.target.track.activeCues[0]
      this.currentCue = cue && cue.text || ''
    },

    hideUI() { 
      this.uiHidden = true  
      this.$store.commit('setHelper', { hidden: true })
    },
    showUI() { 
      this.uiHidden = false 
      this.$store.commit('setHelper', { hidden: false })
    },
    handleMovement(e) {
      this.uiLocked = this.isInteracting(e)
      if (this.uiHidden) {
        this.showUI()
      }
      this.resetTimer()
    },
    isInteracting(e) {
      return (
        e.clientX < 300 ||
        e.clientY > window.innerHeight - 300
      )
    },
    resetTimer() {
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => { 
        if (this.playing && !this.uiLocked) {
          this.hideUI()
        }
      }, 2000) 
    },
  },

  async created() {
    if (this.trackURL) {
      this.trackSRC = this.trackURL && await this.loadTrack(this.trackURL)
    }

    this.timeout = setTimeout(() => {
      if (this.playing && !this.uiLocked) {
        this.hideUI()
      }
    }, 4000)

  },
  
  beforeUnmount() {
    clearTimeout(this.timeout)
    this.showUI()
  },

  watch: {
    desiresTime(newTime) {
      this.$refs.video.currentTime = newTime
      this.play()
    },

    playing(newState) {
      if (newState) {
        this.$refs.video.play()
      } else {
        this.$refs.video.pause()
      }
    },
   
  },
}
</script>

<style scoped>

.videoContainer {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 100%; 
  background: black;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
}

.videoContainer .header {
  position: absolute;
  top: 0;
  padding: 1em;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  max-width: 44em;
  z-index: 1;
  transition: top var(--slow) ease;
}
.videoContainer .header .title span,
.videoContainer .header .descr span {
  background: var(--dark1);
}
.videoContainer .header .title span {
  font-size: 2em;
}
.videoContainer .header .descr {
  margin: 0;
}

.videoContainer video {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  animation: comein 2s ease-in;
}

.videoContainer video::cue { 
  display: none;
}

.videoContainer .controls {
  position: absolute;
  box-sizing: border-box;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 7em;
  padding: 1em;
  display: flex;
  align-items: center;
  transition: bottom var(--slow) ease;
}

.videoContainer .controls .cueContainer {
  position: absolute;
  box-sizing: border-box;
  bottom: 5em;
  max-width: 900px;
  margin-left: 300px;
  font-size: 2em;
  padding: 1em;
  transition: margin var(--slow) ease;
  text-align: center;
}
.videoContainer .controls .cueContainer .cue {
  font-size: 1em;
  color: var(--light1);
  background: var(--dark1);
}
.videoContainer .controls .cueContainer .cue::selection {
  background: var(--light1);
  color: var(--dark1);
}

.videoContainer.hidden .controls {
  bottom: -10em;
  transition: bottom var(--slow) ease;
}
.videoContainer.hidden .controls .cueContainer {
  margin-left: calc(50% - 450px);
}
.videoContainer.hidden .header {
  top: -10em;
  transition: top var(--slow) ease;
}


@keyframes comein {
  0% { opacity: 0;}
  100% { opacity:1;}
}

.isMobile .videoContainer .header {
  top: 2em;
}

.isMobile video {
  object-fit:unset;
  height: unset;
}
.isMobile .videoContainer .controls {
  padding: 0.75em;
  height: 4.5em;
}
</style>
